import type { RichAircraft } from '../data/enrich';

export type IconDef = { file: string; color: string; preserveColor?: boolean };

export const AIRCRAFT_ICON_BASE = '/icons/aircraft/';

/** Pixel size used when rasterizing SVG icons for the map (see `imageFromSvg`). */
export const ICON_CANVAS_PX = 96;

/** Matches `icon-size` stops on the `aircraft-icon` layer in `FlightMap.tsx`. */
export const ICON_SIZE_STOPS: readonly [number, number][] = [
  [2, 0.1],
  [5, 0.15],
  [8, 0.22],
  [12, 0.34],
];

/** Kinds that use dedicated (non–fixed-wing) 3D model scaling. */
export const ICON_MATCHED_FOOTPRINT_KINDS = new Set(['helicopter', 'military-helicopter']);

export function usesIconMatchedFootprint(iconKind: string): boolean {
  return ICON_MATCHED_FOOTPRINT_KINDS.has(iconKind);
}

export function iconSizeAtZoom(zoom: number): number {
  const stops = ICON_SIZE_STOPS;
  if (zoom <= stops[0][0]) return stops[0][1];
  for (let i = 1; i < stops.length; i++) {
    if (zoom <= stops[i][0]) {
      const [z0, s0] = stops[i - 1];
      const [z1, s1] = stops[i];
      const t = (zoom - z0) / (z1 - z0);
      return s0 + t * (s1 - s0);
    }
  }
  return stops[stops.length - 1][1];
}

/** MapLibre `icon-size` expression built from `ICON_SIZE_STOPS`. */
export function buildIconSizeExpression(): unknown[] {
  const branches = ICON_SIZE_STOPS.flatMap(([z, s]) => [z, s]);
  return ['interpolate', ['linear'], ['zoom'], ...branches];
}

export const ICON_SIZE_EXPR = buildIconSizeExpression();

const EMERGENCY_SQUAWKS = new Set(['7700', '7600', '7500']);

export const ICON_DEFS: Record<string, IconDef> = {
  presidential: { file: 'presidential-aircraft.svg', color: '#fde047', preserveColor: true },
  government: { file: 'small-jet.svg', color: '#fbbf24' },
  emergency: { file: 'small-plane.svg', color: '#ef4444' },
  'military-fighter': { file: 'military-jet.svg', color: '#fb923c' },
  'military-transport': { file: 'big-military-plane.svg', color: '#f97316' },
  'military-other': { file: 'big-military-plane.svg', color: '#fdba74' },
  'military-helicopter': { file: 'helicopter.svg', color: '#f97316' },
  helicopter: { file: 'helicopter.svg', color: '#22d3ee' },
  balloon: { file: 'weather-balloon.svg', color: '#f472b6' },
  'heavy-commercial': { file: 'commercial-airliner.svg', color: '#38bdf8' },
  commercial: { file: 'commercial-airliner.svg', color: '#60a5fa' },
  regional: { file: 'small-jet.svg', color: '#7dd3fc' },
  turboprop: { file: 'small-plane.svg', color: '#67e8f9' },
  'private-jet': { file: 'small-jet.svg', color: '#c4b5fd' },
  'light-prop': { file: 'small-plane.svg', color: '#a7f3d0' },
  glider: { file: 'small-plane.svg', color: '#bbf7d0' },
  unknown: { file: 'small-plane.svg', color: '#cbd5e1' },
};

export function isEmergency(a: { squawk: string | null; emergency: string | null }): boolean {
  if (a.squawk && EMERGENCY_SQUAWKS.has(a.squawk)) return true;
  return Boolean(a.emergency && a.emergency !== 'none');
}

/** Map icon kind key used by ICON_DEFS for a rich aircraft record. */
export function resolveAircraftIconKind(aircraft: Pick<RichAircraft, 'specialKind' | 'iconKind'>): string {
  if (aircraft.specialKind === 'presidential') return 'presidential';
  return aircraft.iconKind;
}

export function getIconDef(kind: string, emergency = false): IconDef {
  const base = ICON_DEFS[kind] ?? ICON_DEFS.unknown;
  if (emergency && kind !== 'presidential') {
    return { ...base, color: '#ef4444', preserveColor: false };
  }
  return base;
}

export function normalizeSvgSize(svg: string): string {
  if (!/<svg\b/i.test(svg)) return svg;
  return svg.replace(/<svg\b([^>]*)>/i, (match, attrs: string) => {
    const hasWidth = /\bwidth=/i.test(attrs);
    const hasHeight = /\bheight=/i.test(attrs);
    const additions = `${hasWidth ? '' : ' width="96"'}${hasHeight ? '' : ' height="96"'}`;
    return match.replace(attrs, `${attrs}${additions}`);
  });
}

export function recolorSvg(svg: string, color: string): string {
  return svg
    .replace(/fill:\s*(?!none\b|transparent\b|url\()[^;"}]+/gi, `fill: ${color}`)
    .replace(/stroke:\s*(?!none\b|transparent\b|url\()[^;"}]+/gi, `stroke: ${color}`)
    .replace(/fill="(?!none\b|transparent\b|url\()[^"]+"/gi, `fill="${color}"`)
    .replace(/stroke="(?!none\b|transparent\b|url\()[^"]+"/gi, `stroke="${color}"`);
}

const iconSvgCache = new Map<string, string>();

async function fetchIconSvg(file: string): Promise<string> {
  const cached = iconSvgCache.get(file);
  if (cached) return cached;
  const raw = await fetch(`${AIRCRAFT_ICON_BASE}${file}`).then((res) => (res.ok ? res.text() : ''));
  if (raw) iconSvgCache.set(file, raw);
  return raw;
}

/** Build a data URL for an aircraft icon, matching map icon styling. */
export async function aircraftIconDataUrl(kind: string, emergency = false): Promise<string | null> {
  const def = getIconDef(kind, emergency);
  const raw = await fetchIconSvg(def.file);
  if (!raw) return null;
  const svg = normalizeSvgSize(def.preserveColor ? raw : recolorSvg(raw, def.color));
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export function buildIconImageExpression(): unknown[] {
  const names = Object.keys(ICON_DEFS).flatMap((kind) => [`plane-${kind}`, `plane-${kind}-emergency`]);
  const branches = names.flatMap((name) => [name, name]);
  return ['match', ['get', 'iconImage'], ...branches, 'plane-unknown'];
}

export const ICON_IMAGE_EXPR = buildIconImageExpression();
